import { useEffect, useRef, useState } from 'react'

const CELL = 8
const DAMPING = 0.985
const MAX_FORCE = 6.5
const ENERGY_CUTOFF = 0.04
const SHADE = { r: 96, g: 176, b: 255 }

export default function WaterRipple() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    const hoverMq = window.matchMedia('(hover: hover) and (pointer: fine)')
    const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setEnabled(hoverMq.matches && !motionMq.matches)
    sync()
    hoverMq.addEventListener('change', sync)
    motionMq.addEventListener('change', sync)
    return () => {
      hoverMq.removeEventListener('change', sync)
      motionMq.removeEventListener('change', sync)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    const canvas = canvasRef.current
    const section = canvas?.parentElement
    if (!canvas || !section) return

    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return

    const sim = document.createElement('canvas')
    const simCtx = sim.getContext('2d', { alpha: true })
    if (!simCtx) return

    let cols = 0
    let rows = 0
    let current = new Float32Array(0)
    let previous = new Float32Array(0)
    let pixels: ImageData | null = null
    let raf = 0
    let running = false
    let pointerInside = false
    let lastX = 0
    let lastY = 0
    let lastDripAt = 0
    let glowX = 0
    let glowY = 0
    let glowTargetX = 0
    let glowTargetY = 0
    let glowAlpha = 0

    const idx = (x: number, y: number) => y * cols + x

    const resize = () => {
      const w = section.clientWidth
      const h = section.clientHeight
      if (w <= 0 || h <= 0) return
      canvas.width = w
      canvas.height = h
      cols = Math.max(24, Math.floor(w / CELL))
      rows = Math.max(16, Math.floor(h / CELL))
      sim.width = cols
      sim.height = rows
      current = new Float32Array(cols * rows)
      previous = new Float32Array(cols * rows)
      pixels = simCtx.createImageData(cols, rows)
    }

    const disturb = (cssX: number, cssY: number, force: number) => {
      if (cols < 3 || rows < 3) return
      const gx = Math.max(1, Math.min(cols - 2, (cssX / canvas.width) * cols))
      const gy = Math.max(1, Math.min(rows - 2, (cssY / canvas.height) * rows))
      const cx = Math.round(gx)
      const cy = Math.round(gy)
      const amount = Math.min(MAX_FORCE, force)
      for (let oy = -1; oy <= 1; oy++) {
        for (let ox = -1; ox <= 1; ox++) {
          const falloff = ox === 0 && oy === 0 ? 1 : 0.42
          previous[idx(cx + ox, cy + oy)] += amount * falloff
        }
      }
      start()
    }

    const step = () => {
      if (!pixels) return 0
      let energy = 0
      for (let y = 1; y < rows - 1; y++) {
        for (let x = 1; x < cols - 1; x++) {
          const i = idx(x, y)
          const next =
            ((previous[i - 1] + previous[i + 1] + previous[i - cols] + previous[i + cols]) / 2 - current[i]) *
            DAMPING
          current[i] = next
          const mag = next < 0 ? -next : next
          if (mag > energy) energy = mag
        }
      }
      const swap = previous
      previous = current
      current = swap

      const data = pixels.data
      for (let y = 1; y < rows - 1; y++) {
        for (let x = 1; x < cols - 1; x++) {
          const i = idx(x, y)
          const h = current[i]
          const dx = current[i + 1] - current[i - 1]
          const dy = current[i + cols] - current[i - cols]
          const slope = (dx < 0 ? -dx : dx) + (dy < 0 ? -dy : dy)
          const crest = h < 0 ? -h : h
          const intensity = Math.min(1, crest * 0.09 + slope * 0.14)
          const o = i * 4
          data[o] = SHADE.r
          data[o + 1] = SHADE.g
          data[o + 2] = SHADE.b
          data[o + 3] = intensity * 58
        }
      }
      simCtx.putImageData(pixels, 0, 0)
      return energy
    }

    const drawGlow = () => {
      glowX += (glowTargetX - glowX) * 0.18
      glowY += (glowTargetY - glowY) * 0.18
      glowAlpha += ((pointerInside ? 1 : 0) - glowAlpha) * 0.08
      if (glowAlpha < 0.01) return
      const radius = Math.max(canvas.width, canvas.height) * 0.18
      const g = ctx.createRadialGradient(glowX, glowY, 0, glowX, glowY, radius)
      g.addColorStop(0, `rgba(${SHADE.r}, ${SHADE.g}, ${SHADE.b}, ${0.05 * glowAlpha})`)
      g.addColorStop(0.45, `rgba(${SHADE.r}, ${SHADE.g}, ${SHADE.b}, ${0.02 * glowAlpha})`)
      g.addColorStop(1, `rgba(${SHADE.r}, ${SHADE.g}, ${SHADE.b}, 0)`)
      ctx.fillStyle = g
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }

    const tick = (now: number) => {
      if (pointerInside && now - lastDripAt > 2200) {
        lastDripAt = now
        disturb(glowTargetX, glowTargetY, 1.6)
      }

      const energy = step()
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(sim, 0, 0, canvas.width, canvas.height)
      drawGlow()

      const stillGlowing = glowAlpha > 0.012
      if (energy < ENERGY_CUTOFF && !pointerInside && !stillGlowing) {
        running = false
        raf = 0
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        return
      }
      raf = requestAnimationFrame(tick)
    }

    const start = () => {
      if (running) return
      running = true
      raf = requestAnimationFrame(tick)
    }

    const onMove = (e: PointerEvent) => {
      const rect = section.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      glowTargetX = x
      glowTargetY = y
      if (!pointerInside) {
        pointerInside = true
        glowX = x
        glowY = y
        lastX = x
        lastY = y
        lastDripAt = performance.now()
        disturb(x, y, 2.4)
        return
      }
      const dx = x - lastX
      const dy = y - lastY
      const dist = Math.hypot(dx, dy)
      if (dist > 10) {
        disturb(x, y, Math.min(MAX_FORCE, 1.1 + dist * 0.08))
        lastX = x
        lastY = y
      }
    }

    const onLeave = () => {
      pointerInside = false
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(section)
    section.addEventListener('pointermove', onMove, { passive: true })
    section.addEventListener('pointerleave', onLeave)

    return () => {
      running = false
      if (raf) cancelAnimationFrame(raf)
      observer.disconnect()
      section.removeEventListener('pointermove', onMove)
      section.removeEventListener('pointerleave', onLeave)
    }
  }, [enabled])

  if (!enabled) return null

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 z-[1] h-full w-full mix-blend-screen opacity-80"
    />
  )
}
